import React, { useState, useEffect, useRef } from 'react';
import styles from './ClientFilterSidebar.module.css';
import FilterIcon from '../Icons/FilterIcon';
import axios from 'axios';

import CustomDropdown from './CustomDropdown';
import MultiSelectDropdown from './MultiSelectDropdown';
import SimpleMultiSelectDropdown from './SimpleMultiSelectDropdown';
import CustomDateInput from './CustomDateInput';
import { getTherapistList, getServicesList, getIndividualServicesList, exportCustomers, Hipster_Base_URL } from '../../APICalling/APICalling';
import Echo from '../Sockect/Echo';
import { toast } from 'react-toastify';
import { useGlobalContext } from '../../Context/GlobalContext';

const ClientFilterSidebar = ({ isOpen, onClose, onApplyFilters }) => {
    const { selectedOutlet, loggedOutletID } = useGlobalContext();
    const [filters, setFilters] = useState({

        clientProfileCreated: 'All Time',
        hadFirstAppointment: 'All Time',
        hadAppointment: 'All Time',
        hadAppointmentWith: [],
        hadAppointmentWithEmpty: false,
        futureAppointment: '',
        hadService: '',
        hadServiceInCategory: [],
        membershipStatus: '',
        gender: 'Any gender',
        referredBy: '',
        tag: '',
        lastAppointment: false,
        daysAgo: '',

        includeNoAppointments: false,
        // Independent date ranges per section
        clientProfileStartDate: '',
        clientProfileEndDate: '',
        hadFirstStartDate: '',
        hadFirstEndDate: '',
        hadAppointmentStartDate: '',
        hadAppointmentEndDate: ''
    });

    const [dateErrorClientProfile, setDateErrorClientProfile] = useState('');
    const [dateErrorHadFirst, setDateErrorHadFirst] = useState('');
    const [dateErrorHadAppointment, setDateErrorHadAppointment] = useState('');
    const [hadAppointmentWithTouched, setHadAppointmentWithTouched] = useState(false);

    // API data states
    const [therapists, setTherapists] = useState([]);
    const [services, setServices] = useState([]);
    const [individualServices, setIndividualServices] = useState([]);
    const [serviceCategories, setServiceCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    // Therapist states (simplified - no pagination needed)
    const [hasMoreTherapists, setHasMoreTherapists] = useState(false);

    // Ref for auto-scrolling
    const filterContentRef = useRef(null);
    // Ref to track last fetched outlet to refetch when outlet changes
    const lastFetchedOutletRef = useRef(null);

    // Helper to smoothly scroll the filter content to bottom (used in multiple onOpen handlers)
    const scrollToBottomSmooth = () => {
        setTimeout(() => {
            if (filterContentRef.current) {
                filterContentRef.current.scrollTo({
                    top: filterContentRef.current.scrollHeight,
                    behavior: 'smooth'
                });
            }
        }, 0);
    };

    // Fetch therapists for a specific outlet
    const fetchTherapistsForOutlet = async (outletId) => {
        if (!outletId) {
            return;
        }
        
        // Skip if we already fetched for this outlet and have therapists
        if (lastFetchedOutletRef.current === outletId && therapists.length > 0) {
            return;
        }
        
        try {
            setLoading(true);

            const response = await getTherapistList({
                outlet: outletId,
                pagination: 0,
                page: 1
            });
            
            if (response?.data) {
                // API returns an array in data.data.list.staffs
                const allStaff = response.data?.data?.data?.list?.staffs || [];

                // Some environments don't send a 'role' field. Fall back to status/alias.
                const therapistsOnly = allStaff.filter((staff) => {
                    const role = (staff.role || '').toString().toLowerCase();
                    const isReception = role.includes('receptionist');
                    // If role is missing, assume it's a therapist; otherwise require it to include 'staff'
                    const isStaff = role ? role.includes('staff') : true;
                    const isActive = Number(staff.status) === 1 || staff.status === '1';
                    // Keep only active non‑reception staff
                    return isActive && !isReception && isStaff;
                });

                setTherapists(therapistsOnly);
                setHasMoreTherapists(false);
                // Track that we fetched for this outlet
                lastFetchedOutletRef.current = outletId;
            } else {
                console.error(' API request failed:', response);
                setHasMoreTherapists(false);
            }
        } catch (error) {
            console.error(' Error fetching therapists:', error);
            setHasMoreTherapists(false);
        } finally {
            setLoading(false);
        }
    };

 

    // Fetch all services and categories at once
    const fetchServices = async () => {
        try {
         
            
            // Fetch all service categories (disable pagination)
            const categoryResponse = await getServicesList({
                pagination: 0,  // Disable pagination to get all data
                page: 1
            });
            
            if (categoryResponse?.data?.success) {
                const categoriesData = categoryResponse.data.data?.list?.category || [];
            
                setServices(categoriesData);
                
                // Extract category data for "Had service in category" filter
                const categoryData = categoriesData.map(category => ({
                    id: category.id,
                    name: category.name
                })).filter(category => category.name);
                setServiceCategories(categoryData);
                
                // Extract individual services from all categories for "Had service" filter
                const allIndividualServices = [];
                categoriesData.forEach(category => {
                    if (category.services && Array.isArray(category.services)) {
                        category.services.forEach(service => {
                            allIndividualServices.push({
                                id: service.id,
                                name: service.name,
                                category: category.name,
                                duration: service.duration,
                                price: service.price,
                                member_price: service.member_price
                            });
                        });
                    }
                });
           
                setIndividualServices(allIndividualServices);
            }

            // Try to fetch individual services from separate API (fallback) - also load all
            try {
                const individualResponse = await getIndividualServicesList({
                    pagination: 0,  // Disable pagination to get all data
                    page: 1
                });
                if (individualResponse?.data?.success) {
                    const individualServicesData = individualResponse.data.data?.list || [];
                    // Only use this if we got actual data and it's different from what we extracted
                    if (individualServicesData.length > 0) {
                     
                        setIndividualServices(individualServicesData);
                    }
                }
            } catch (individualError) {
                console.error('Error fetching individual services from separate API:', individualError);
                // We already have individual services extracted from categories, so no need for fallback
            }
        } catch (error) {
            console.error('Error fetching services:', error);
        }
    };

    // No longer needed - we load all therapists at once
 

    useEffect(() => {
        if (!isOpen) return;
        setHadAppointmentWithTouched(false);
        
        // Use selectedOutlet or fallback to loggedOutletID
        const outletId = selectedOutlet || loggedOutletID;
        
        // Fetch therapists when sidebar opens and outlet is available
        if (outletId) {
            // Always fetch if we don't have therapists OR if outlet changed
            const outletChanged = lastFetchedOutletRef.current !== null && lastFetchedOutletRef.current !== outletId;
            const noTherapists = therapists.length === 0;
            
            if (outletChanged) {
                // Clear therapists when outlet changes
                setTherapists([]);
                lastFetchedOutletRef.current = null;
            }
            
            if (outletChanged || noTherapists) {
                fetchTherapistsForOutlet(outletId);
            }
        }
        
        // Fetch services if not already loaded
        if (services.length === 0 || individualServices.length === 0 || serviceCategories.length === 0) {
            fetchServices();
        }
    }, [isOpen, selectedOutlet, loggedOutletID]);

    // Set all therapists as default when therapists are loaded (only if filter is empty)
    useEffect(() => {
        if (therapists.length > 0) {
            setFilters(prev => {
                // Only set default if hadAppointmentWith is empty (initial state)
                if (prev.hadAppointmentWith.length === 0 && !prev.hadAppointmentWithEmpty) {
                    const allTherapistIds = therapists.map(t => t.id);
                    return { ...prev, hadAppointmentWith: allTherapistIds };
                }
                return prev;
            });
        }
    }, [therapists.length]); // Only depend on therapists.length to avoid infinite loops

    const handleFilterChange = (filterName, value) => {
        setFilters(prev => {
            const next = { ...prev, [filterName]: value };
            if (filterName === 'hadAppointmentWith') {
                setHadAppointmentWithTouched(true);
                next.hadAppointmentWithEmpty = Array.isArray(value) ? value.length === 0 : false;
            }
            // Per-section date validation
            if (filterName === 'clientProfileStartDate' || filterName === 'clientProfileEndDate') {
                if (next.clientProfileStartDate && next.clientProfileEndDate) {
                    const start = new Date(next.clientProfileStartDate);
                    const end = new Date(next.clientProfileEndDate);
                    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && start > end) {
                        setDateErrorClientProfile('Start date cannot be after end date.');
                    } else {
                        setDateErrorClientProfile('');
                    }
                } else {
                    setDateErrorClientProfile('');
                }
            }
            if (filterName === 'hadFirstStartDate' || filterName === 'hadFirstEndDate') {
                if (next.hadFirstStartDate && next.hadFirstEndDate) {
                    const start = new Date(next.hadFirstStartDate);
                    const end = new Date(next.hadFirstEndDate);
                    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && start > end) {
                        setDateErrorHadFirst('Start date cannot be after end date.');
                    } else {
                        setDateErrorHadFirst('');
                    }
                } else {
                    setDateErrorHadFirst('');
                }
            }
            if (filterName === 'hadAppointmentStartDate' || filterName === 'hadAppointmentEndDate') {
                if (next.hadAppointmentStartDate && next.hadAppointmentEndDate) {
                    const start = new Date(next.hadAppointmentStartDate);
                    const end = new Date(next.hadAppointmentEndDate);
                    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && start > end) {
                        setDateErrorHadAppointment('Start date cannot be after end date.');
                    } else {
                        setDateErrorHadAppointment('');
                    }
                } else {
                    setDateErrorHadAppointment('');
                }
            }
            return next;
        });

        // Auto-scroll to bottom when Last appointment toggle is turned on
        if (filterName === 'lastAppointment' && value === true) {
            setTimeout(() => {
                if (filterContentRef.current) {
                    filterContentRef.current.scrollTo({
                        top: filterContentRef.current.scrollHeight,
                        behavior: 'smooth'
                    });
                }
            }, 100); // Small delay to ensure the new content is rendered
        }
    };

    const handleClearAll = () => {
        // Set hadAppointmentWith to all therapists when clearing (default state)
        const allTherapistIds = therapists.map(t => t.id);
        const clearedFilters = {
            clientProfileCreated: 'All Time',
            hadFirstAppointment: 'All Time',
            hadAppointment: 'All Time',
            hadAppointmentWith: allTherapistIds,
            hadAppointmentWithEmpty: false,
            futureAppointment: '',
            hadService: '',
            hadServiceInCategory: [],
            membershipStatus: '',
            gender: 'Any gender',
            referredBy: '',
            tag: '',
            lastAppointment: false,
            daysAgo: '',
            includeNoAppointments: false,
            clientProfileStartDate: '',
            clientProfileEndDate: '',
            hadFirstStartDate: '',
            hadFirstEndDate: '',
            hadAppointmentStartDate: '',
            hadAppointmentEndDate: ''
        };
        
        setFilters(clearedFilters);
        setDateErrorClientProfile('');
        setDateErrorHadFirst('');
        setDateErrorHadAppointment('');
        setHadAppointmentWithTouched(false);
        
        // Don't send hadAppointmentWith when all are selected (treat as no filter)
        const clearedForAPI = { ...clearedFilters, hadAppointmentWith: [] };
        
        // Call API with cleared filters but don't close sidebar
        onApplyFilters(clearedForAPI);
    };

    // Helper function to get current date info
    
    // Helper function to get current date info
const getCurrentDateInfo = () => {
    const now = new Date();
    const today = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    
    // Get week info - create separate date objects to avoid mutation
    // For Monday to Sunday week format
    const thisWeekStart = new Date(now);
    const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // If Sunday, go back 6 days to Monday
    thisWeekStart.setDate(now.getDate() - daysFromMonday); // Start of this week (Monday)
    const thisWeekEnd = new Date(thisWeekStart);
    thisWeekEnd.setDate(thisWeekStart.getDate() + 6); // End of this week (Sunday)
    const thisWeek = `${thisWeekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${thisWeekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    
    // Last week: 7 days before this week's start
    const lastWeekStart = new Date(thisWeekStart);
    lastWeekStart.setDate(thisWeekStart.getDate() - 7);
    const lastWeekEnd = new Date(lastWeekStart);
    lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
    const lastWeek = `${lastWeekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${lastWeekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    
    // Last two weeks: from 2 weeks ago to end of last week
    const twoWeeksAgoStart = new Date(lastWeekStart);
    twoWeeksAgoStart.setDate(lastWeekStart.getDate() - 7); // 7 days before last week's start
    const lastTwoWeeks = `${twoWeeksAgoStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${lastWeekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    
    // Get dynamic month names for last 3 months
    const currentMonth = now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const previousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const twoMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    
    return { today, yesterday, thisWeek, lastWeek, lastTwoWeeks, currentMonth, previousMonth, twoMonthsAgo };
};

    // Check if any filters are selected (excluding default values)
    const hasActiveFilters = () => {
        if (hadAppointmentWithTouched) return true;
        if (filters.hadAppointmentWithEmpty) return true;
        // Check if all therapists are selected
        const allTherapistIds = therapists.map(t => t.id);
        const isAllTherapistsSelected = therapists.length > 0 && 
            filters.hadAppointmentWith.length === therapists.length &&
            allTherapistIds.every(id => filters.hadAppointmentWith.includes(id));
        
        return (
            filters.clientProfileCreated !== 'All Time' ||
            filters.hadFirstAppointment !== 'All Time' ||
            filters.hadAppointment !== 'All Time' ||
            (filters.hadAppointmentWith.length > 0 && !isAllTherapistsSelected) ||
            filters.futureAppointment !== '' ||
            filters.hadService !== '' ||
            filters.hadServiceInCategory.length > 0 ||
            filters.membershipStatus !== '' ||
            filters.gender !== 'Any gender' ||
            filters.referredBy !== '' ||
            filters.tag !== '' ||
            filters.lastAppointment !== false ||
            (filters.lastAppointment && (filters.daysAgo !== '' || filters.includeNoAppointments))
        );
    };

    const handleApply = () => {
        // Validate each selected Custom Time Period independently
        if (filters.clientProfileCreated === 'Custom Time Period') {
            // Check if both dates are filled
            if (!filters.clientProfileStartDate || !filters.clientProfileEndDate) {
                setDateErrorClientProfile('Both start date and end date are required.');
                return;
            }
            // Check if start date is after end date
            const start = new Date(filters.clientProfileStartDate);
            const end = new Date(filters.clientProfileEndDate);
            if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && start > end) {
                setDateErrorClientProfile('Start date cannot be after end date.');
                return;
            }
            // Clear error if validation passes
            setDateErrorClientProfile('');
        }
        if (filters.hadFirstAppointment === 'Custom Time Period') {
            // Check if both dates are filled
            if (!filters.hadFirstStartDate || !filters.hadFirstEndDate) {
                setDateErrorHadFirst('Both start date and end date are required.');
                return;
            }
            // Check if start date is after end date
            const start = new Date(filters.hadFirstStartDate);
            const end = new Date(filters.hadFirstEndDate);
            if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && start > end) {
                setDateErrorHadFirst('Start date cannot be after end date.');
                return;
            }
            // Clear error if validation passes
            setDateErrorHadFirst('');
        }
        if (filters.hadAppointment === 'Custom Time Period') {
            // Check if both dates are filled
            if (!filters.hadAppointmentStartDate || !filters.hadAppointmentEndDate) {
                setDateErrorHadAppointment('Both start date and end date are required.');
                return;
            }
            // Check if start date is after end date
            const start = new Date(filters.hadAppointmentStartDate);
            const end = new Date(filters.hadAppointmentEndDate);
            if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && start > end) {
                setDateErrorHadAppointment('Start date cannot be after end date.');
                return;
            }
            // Clear error if validation passes
            setDateErrorHadAppointment('');
        }
        // Convert date formats to match API requirements
        const processedFilters = { ...filters };
        
        // If all therapists are selected, don't send hadAppointmentWith (treat as no filter)
        const allTherapistIds = therapists.map(t => t.id);
        const isAllTherapistsSelected = therapists.length > 0 && 
            processedFilters.hadAppointmentWith.length === therapists.length &&
            allTherapistIds.every(id => processedFilters.hadAppointmentWith.includes(id));
        if (isAllTherapistsSelected) {
            processedFilters.hadAppointmentWith = [];
            processedFilters.hadAppointmentWithEmpty = false;
        } else if (hadAppointmentWithTouched && processedFilters.hadAppointmentWith.length === 0) {
            processedFilters.hadAppointmentWithEmpty = true;
        }
        
        // Convert each section's date inputs to DD-MM-YYYY format for API
        if (processedFilters.clientProfileStartDate) {
            const d = new Date(processedFilters.clientProfileStartDate);
            processedFilters.clientProfileStartDate = d.toLocaleDateString('en-GB').split('/').reverse().join('-');
        }
        if (processedFilters.clientProfileEndDate) {
            const d = new Date(processedFilters.clientProfileEndDate);
            processedFilters.clientProfileEndDate = d.toLocaleDateString('en-GB').split('/').reverse().join('-');
        }
        if (processedFilters.hadFirstStartDate) {
            const d = new Date(processedFilters.hadFirstStartDate);
            processedFilters.hadFirstStartDate = d.toLocaleDateString('en-GB').split('/').reverse().join('-');
        }
        if (processedFilters.hadFirstEndDate) {
            const d = new Date(processedFilters.hadFirstEndDate);
            processedFilters.hadFirstEndDate = d.toLocaleDateString('en-GB').split('/').reverse().join('-');
        }
        if (processedFilters.hadAppointmentStartDate) {
            const d = new Date(processedFilters.hadAppointmentStartDate);
            processedFilters.hadAppointmentStartDate = d.toLocaleDateString('en-GB').split('/').reverse().join('-');
        }
        if (processedFilters.hadAppointmentEndDate) {
            const d = new Date(processedFilters.hadAppointmentEndDate);
            processedFilters.hadAppointmentEndDate = d.toLocaleDateString('en-GB').split('/').reverse().join('-');
        }
        
        onApplyFilters(processedFilters);
        setHadAppointmentWithTouched(false);
        onClose();
    };

    // Build export params identical to list API mapping
    const buildExportParams = () => {
        const params = {};

        const toRangeString = (start, end) => `${start} / ${end}`;

        // Helper: convert predefined labels to actual ranges (DD-MM-YYYY)
        const convertDateRange = (label) => {
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

            const fmt = (d) => d.toLocaleDateString('en-GB').split('/').reverse().join('-');
            const { today: t, yesterday: y, thisWeek, lastWeek, lastTwoWeeks, currentMonth, previousMonth, twoMonthsAgo } = getCurrentDateInfo();

            switch (label) {
                case `Today (${t})`:
                    return { start: fmt(today), end: fmt(today) };
                case `Yesterday (${y})`:
                    const yd = new Date(today); yd.setDate(yd.getDate() - 1);
                    return { start: fmt(yd), end: fmt(yd) };
                case `This Week (${thisWeek})`:
                    // Monday to Sunday
                    const startOfWeek = new Date(today);
                    const dow = today.getDay();
                    const fromMonday = dow === 0 ? 6 : dow - 1;
                    startOfWeek.setDate(today.getDate() - fromMonday);
                    const endOfWeek = new Date(startOfWeek); endOfWeek.setDate(startOfWeek.getDate() + 6);
                    return { start: fmt(startOfWeek), end: fmt(endOfWeek) };
                case `Last Week (${lastWeek})`:
                    const thisWeekStart = new Date(today);
                    const dow2 = today.getDay();
                    const fromMon2 = dow2 === 0 ? 6 : dow2 - 1;
                    thisWeekStart.setDate(today.getDate() - fromMon2);
                    const lastWeekStart = new Date(thisWeekStart); lastWeekStart.setDate(thisWeekStart.getDate() - 7);
                    const lastWeekEnd = new Date(lastWeekStart); lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
                    return { start: fmt(lastWeekStart), end: fmt(lastWeekEnd) };
                case `Last Two Weeks (${lastTwoWeeks})`:
                    const twoWeeksStart = new Date(today); twoWeeksStart.setDate(today.getDate() - 14);
                    return { start: fmt(twoWeeksStart), end: fmt(today) };
                case currentMonth:
                case previousMonth:
                case twoMonthsAgo: {
                    // Parse Month YYYY
                    const [monthName, yearStr] = label.split(' ');
                    const monthIndex = new Date(`${monthName} 1, ${yearStr}`).getMonth();
                    const yearNum = parseInt(yearStr, 10);
                    const start = new Date(yearNum, monthIndex, 1);
                    const end = new Date(yearNum, monthIndex + 1, 0);
                    return { start: fmt(start), end: fmt(end) };
                }
                default:
                    return null;
            }
        };

        // Client profile created
        if (filters.clientProfileCreated && filters.clientProfileCreated !== 'All Time') {
            if (filters.clientProfileCreated === 'Custom Time Period') {
                // Only include if both dates are provided
                if (filters.clientProfileStartDate && filters.clientProfileEndDate) {
                    params.profile_daterange = toRangeString(
                        new Date(filters.clientProfileStartDate).toLocaleDateString('en-GB').split('/').reverse().join('-'),
                        new Date(filters.clientProfileEndDate).toLocaleDateString('en-GB').split('/').reverse().join('-')
                    );
                }
                // If dates are missing, don't include the parameter (validation should have caught this)
            } else {
                const dr = convertDateRange(filters.clientProfileCreated);
                if (dr) params.profile_daterange = toRangeString(dr.start, dr.end);
            }
        }

        // Had first appointment
        if (filters.hadFirstAppointment && filters.hadFirstAppointment !== 'All Time') {
            if (filters.hadFirstAppointment === 'Custom Time Period') {
                // Only include if both dates are provided
                if (filters.hadFirstStartDate && filters.hadFirstEndDate) {
                    params.first_appointment_daterange = toRangeString(
                        new Date(filters.hadFirstStartDate).toLocaleDateString('en-GB').split('/').reverse().join('-'),
                        new Date(filters.hadFirstEndDate).toLocaleDateString('en-GB').split('/').reverse().join('-')
                    );
                }
                // If dates are missing, don't include the parameter (validation should have caught this)
            } else {
                const dr = convertDateRange(filters.hadFirstAppointment);
                if (dr) params.first_appointment_daterange = toRangeString(dr.start, dr.end);
            }
        }

        // Had appointment
        if (filters.hadAppointment && filters.hadAppointment !== 'All Time') {
            if (filters.hadAppointment === 'Custom Time Period') {
                // Only include if both dates are provided
                if (filters.hadAppointmentStartDate && filters.hadAppointmentEndDate) {
                    params.appointment_daterange = toRangeString(
                        new Date(filters.hadAppointmentStartDate).toLocaleDateString('en-GB').split('/').reverse().join('-'),
                        new Date(filters.hadAppointmentEndDate).toLocaleDateString('en-GB').split('/').reverse().join('-')
                    );
                }
                // If dates are missing, don't include the parameter (validation should have caught this)
            } else {
                const dr = convertDateRange(filters.hadAppointment);
                if (dr) params.appointment_daterange = toRangeString(dr.start, dr.end);
            }
        }

        // Had appointment with (IDs comma-separated)
        // Only send if not all therapists are selected
        const allTherapistIds = therapists.map(t => t.id);
        const isAllTherapistsSelected = therapists.length > 0 && 
            filters.hadAppointmentWith.length === therapists.length &&
            allTherapistIds.every(id => filters.hadAppointmentWith.includes(id));
        if (filters.hadAppointmentWith && filters.hadAppointmentWith.length > 0 && !isAllTherapistsSelected) {
            params.appointment_with = filters.hadAppointmentWith.join(',');
        }

        // Future appointment flags
        if (filters.futureAppointment) {
            if (filters.futureAppointment === 'Has future appointment') {
                params.future_appointment = true;
            } else if (filters.futureAppointment === 'Has no future appointment') {
                params.no_future_appointment = true;
            }
        }

        // Had service (service IDs comma-separated or single id)
        if (filters.hadService) {
            params.had_service = filters.hadService;
        }

        // Had service in category (IDs comma-separated)
        if (filters.hadServiceInCategory && filters.hadServiceInCategory.length > 0) {
            params.service_category = filters.hadServiceInCategory.join(',');
        }

        // Membership status
        if (filters.membershipStatus !== '') {
            params.membership_status = filters.membershipStatus;
        }

        // Gender
        if (filters.gender && filters.gender !== 'Any gender') {
            params.gender = String(filters.gender).toLowerCase();
        }

        // Referred by / tag
        if (filters.referredBy) params.referred_by = filters.referredBy;
        if (filters.tag) params.tag = filters.tag;

        // Last appointment related
        if (filters.lastAppointment) params.last_appointment = true;
        if (filters.daysAgo) params.days_ago = filters.daysAgo;
        if (filters.includeNoAppointments) params.include_no_appointments = true;

        return params;
    };


    const handleDownload = async () => {
        // Get user_id and token from localStorage
        const userId = localStorage.getItem('user_id');
        const token = localStorage.getItem('token');
        
        if (!userId) {
            toast.error('User ID not found');
            return;
        }

        if (!token) {
            toast.error('Authentication token not found');
            return;
        }

        // Create user-specific private channel name (matching pattern used in other reports)
        const channelName = `export-clients.${userId}`;
        let channel;
        
        setIsExporting(true);
        
        try {
            // Ensure no duplicate listeners from previous runs
            try { Echo.leave?.(channelName); } catch {}
            // Use private channel with user-specific channel name
            channel = Echo.private(channelName);
        } catch (e) {
            console.warn('[Export] Private subscribe failed:', e);
            setIsExporting(false);
            toast.error('Failed to connect to export channel');
            return;
        }
    
        const cleanup = () => {
            try {
                ['export.status.updated', '.export.status.updated', ]
                .forEach(evt => { try { channel.stopListening(evt); } catch {} });
            } catch {}
            try { Echo.leave?.(channelName); } catch {}
        
        };

        const handleEvent = async (event) => {
            try {
                // Check the status of the export (exactly like LeaveReport)
                const status = event?.status || event?.data?.status;

                // If still processing, just wait for the next event
                if (status === 'processing' || status === 'queued') {
                    return; // Don't cleanup, keep listening
                }

                // If failed, show error
                if (status === 'failed' || status === 'error') {
                    const errorMessage = event?.message || event?.data?.message || 'Export failed';
                    toast.error(errorMessage);
                    cleanup();
                    setIsExporting(false);
                    return;
                }

                // If completed, extract download URL (exactly like LeaveReport)
                const downloadUrl = event?.data?.download_url || 
                                  event?.download_url ||
                                  event?.data?.data?.download_url;

                const filename = event?.data?.filename || 
                               event?.filename ||
                               `clients_export_${new Date().getTime()}.csv`;

                if (!downloadUrl) {
                    toast.error('Download URL not received. Please try again.');
                    cleanup();
                    setIsExporting(false);
                    return;
                }

                // Trigger download directly with the URL from the event (exactly like LeaveReport)
                try {
                    const link = document.createElement('a');
                    link.href = downloadUrl;
                    link.target = '_blank';
                    link.download = filename;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);

                    toast.success('Downloaded successfully');
                } catch (downloadError) {
                    toast.error('Failed to start download');
                }

                cleanup();
                setIsExporting(false);
            } catch (error) {
                toast.error('Failed to process download');
                cleanup();
                setIsExporting(false);
            }
        };
    
        
    
        try {
            // Listen to the event (single event name like LeaveReport)
            channel.listen('export.status.updated', handleEvent);
    
            // Direct Pusher bind as backup (like LeaveReport)
            if (Echo?.connector?.pusher) {
                const pusherChannel = Echo.connector.pusher.channel(`private-${channelName}`);
                if (pusherChannel && typeof pusherChannel.bind === 'function') {
                    pusherChannel.bind('export.status.updated', handleEvent);
                }
            }
    
            // Error handler (like LeaveReport)
            if (typeof channel.error === 'function') {
                channel.error((error) => {
                    toast.error('Socket connection error');
                    cleanup();
                    setIsExporting(false);
                });
            }
    
            // Wait for subscription to be ready before calling API (like LeaveReport)
            await new Promise((resolve) => setTimeout(resolve, 500));
    
            // Fire export request
            const params = buildExportParams();
            const resp = await exportCustomers(params);
        } catch (error) {
            console.error('[Export] Export failed to start:', error);
            cleanup();
            setIsExporting(false);
            try { toast.error('Failed to start export'); } catch {}
        }
    };
    

    if (!isOpen) return null;

    return (

         <div className={`${styles.filterOverlay} ${isOpen ? styles.open : ''}`} onClick={onClose}>
             <div className={`${styles.filterSidebar} ${isOpen ? styles.open : ''}`} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className={styles.filterHeader}>
                    <div></div> {/* Empty div for flex spacing */}
                    <button className={styles.closeButton} onClick={onClose}>
                        ×
                    </button>
                </div>

                {/* Filter Content */}
                <div className={styles.filterContent} ref={filterContentRef}>
                    {/* Client profile created */}
                    <div className={styles.filterGroup}>
                        <label className={styles.filterLabel}>Client profile created</label>

                        <CustomDropdown
                            options={[
                                { value: 'All Time', label: 'All Time' },
                                { value: `Today (${getCurrentDateInfo().today})`, label: `Today (${getCurrentDateInfo().today})` },
                                { value: `Yesterday (${getCurrentDateInfo().yesterday})`, label: `Yesterday (${getCurrentDateInfo().yesterday})` },
                                { value: `This Week (${getCurrentDateInfo().thisWeek})`, label: `This Week (${getCurrentDateInfo().thisWeek})` },
                                { value: `Last Week (${getCurrentDateInfo().lastWeek})`, label: `Last Week (${getCurrentDateInfo().lastWeek})` },
                                { value: `Last Two Weeks (${getCurrentDateInfo().lastTwoWeeks})`, label: `Last Two Weeks (${getCurrentDateInfo().lastTwoWeeks})` },
                                { value: 'Custom Time Period', label: 'Custom Time Period' },
                                { value: getCurrentDateInfo().currentMonth, label: getCurrentDateInfo().currentMonth },
                                { value: getCurrentDateInfo().previousMonth, label: getCurrentDateInfo().previousMonth },
                                { value: getCurrentDateInfo().twoMonthsAgo, label: getCurrentDateInfo().twoMonthsAgo }
                            ]}
                                value={filters.clientProfileCreated}

                            onChange={(value) => handleFilterChange('clientProfileCreated', value)}
                            placeholder="Select time period"
                            disableSearch={true}
                        />
                        
                                                 {/* Custom date inputs - show only when Custom Time Period is selected */}
                         {filters.clientProfileCreated === 'Custom Time Period' && (
                             <>
                                 <div className={styles.customDateInputs}>
                                     <div className={styles.dateInputContainer}>
                                         <CustomDateInput
                                             value={filters.clientProfileStartDate}
                                             onChange={(value) => handleFilterChange('clientProfileStartDate', value)}
                                             className={styles.dateInput}
                                             placeholder="Start date"
                                         />
                                     </div>
                                     <div className={styles.dateInputContainer}>
                                         <CustomDateInput
                                             value={filters.clientProfileEndDate}
                                             onChange={(value) => handleFilterChange('clientProfileEndDate', value)}
                                             className={styles.dateInput}
                                             placeholder="End date"
                                         />
                                     </div>
                                 </div>
                                 {dateErrorClientProfile && (
                                     <div style={{ color: '#dc2626', fontSize: 12, marginTop: 6 }}>{dateErrorClientProfile}</div>
                                 )}
                             </>
                         )}
                    </div>

                    {/* Membership Status */}
                    <div className={styles.filterGroup}>
                        <label className={styles.filterLabel}>Membership Status</label>
                        <CustomDropdown
                            options={[
                                { value: '', label: 'Select status' },
                                { value: 'active', label: 'Active' },
                                { value: 'expired', label: 'Expired' }
                            ]}
                            value={filters.membershipStatus}
                            onChange={(value) => handleFilterChange('membershipStatus', value)}
                            placeholder="Select status"
                            autoFocusSearch
                        />
                    </div>

                    {/* Had first appointment */}
                    {false && (
                    <div className={styles.filterGroup}>
                        <label className={styles.filterLabel}>Had first appointment</label>

                        <CustomDropdown
                            options={[
                                { value: 'All Time', label: 'All Time' },
                                { value: `Today (${getCurrentDateInfo().today})`, label: `Today (${getCurrentDateInfo().today})` },
                                { value: `Yesterday (${getCurrentDateInfo().yesterday})`, label: `Yesterday (${getCurrentDateInfo().yesterday})` },
                                { value: `This Week (${getCurrentDateInfo().thisWeek})`, label: `This Week (${getCurrentDateInfo().thisWeek})` },
                                { value: `Last Week (${getCurrentDateInfo().lastWeek})`, label: `Last Week (${getCurrentDateInfo().lastWeek})` },
                                { value: `Last Two Weeks (${getCurrentDateInfo().lastTwoWeeks})`, label: `Last Two Weeks (${getCurrentDateInfo().lastTwoWeeks})` },
                                { value: 'Custom Time Period', label: 'Custom Time Period' },
                                { value: getCurrentDateInfo().currentMonth, label: getCurrentDateInfo().currentMonth },
                                { value: getCurrentDateInfo().previousMonth, label: getCurrentDateInfo().previousMonth },
                                { value: getCurrentDateInfo().twoMonthsAgo, label: getCurrentDateInfo().twoMonthsAgo }
                            ]}
                                value={filters.hadFirstAppointment}

                            onChange={(value) => handleFilterChange('hadFirstAppointment', value)}
                            placeholder="Select time period"
                            disableSearch={true}
                        />
                        
                         {filters.hadFirstAppointment === 'Custom Time Period' && (
                             <>
                                 <div className={styles.customDateInputs}>
                                     <div className={styles.dateInputContainer}>
                                         <CustomDateInput
                                             value={filters.hadFirstStartDate}
                                             onChange={(value) => handleFilterChange('hadFirstStartDate', value)}
                                             className={styles.dateInput}
                                             placeholder="Start date"
                                         />
                                     </div>
                                     <div className={styles.dateInputContainer}>
                                         <CustomDateInput
                                             value={filters.hadFirstEndDate}
                                             onChange={(value) => handleFilterChange('hadFirstEndDate', value)}
                                             className={styles.dateInput}
                                             placeholder="End date"
                                         />
                                     </div>
                                 </div>
                                 {dateErrorHadFirst && (
                                     <div style={{ color: '#dc2626', fontSize: 12, marginTop: 6 }}>{dateErrorHadFirst}</div>
                                 )}
                             </>
                         )}
                    </div>
                    )}

                    {/* Had appointment */}
                    {false && (
                    <div className={styles.filterGroup}>
                        <label className={styles.filterLabel}>Had appointment</label>

                        <CustomDropdown
                            options={[
                                { value: 'All Time', label: 'All Time' },
                                { value: `Today (${getCurrentDateInfo().today})`, label: `Today (${getCurrentDateInfo().today})` },
                                { value: `Yesterday (${getCurrentDateInfo().yesterday})`, label: `Yesterday (${getCurrentDateInfo().yesterday})` },
                                { value: `This Week (${getCurrentDateInfo().thisWeek})`, label: `This Week (${getCurrentDateInfo().thisWeek})` },
                                { value: `Last Week (${getCurrentDateInfo().lastWeek})`, label: `Last Week (${getCurrentDateInfo().lastWeek})` },
                                { value: `Last Two Weeks (${getCurrentDateInfo().lastTwoWeeks})`, label: `Last Two Weeks (${getCurrentDateInfo().lastTwoWeeks})` },
                                { value: 'Custom Time Period', label: 'Custom Time Period' },
                                { value: getCurrentDateInfo().currentMonth, label: getCurrentDateInfo().currentMonth },
                                { value: getCurrentDateInfo().previousMonth, label: getCurrentDateInfo().previousMonth },
                                { value: getCurrentDateInfo().twoMonthsAgo, label: getCurrentDateInfo().twoMonthsAgo }
                            ]}
                                value={filters.hadAppointment}

                            onChange={(value) => handleFilterChange('hadAppointment', value)}
                            placeholder="Select time period"
                            disableSearch={true}
                        />
                        
                         {filters.hadAppointment === 'Custom Time Period' && (
                             <>
                                 <div className={styles.customDateInputs}>
                                     <div className={styles.dateInputContainer}>
                                         <CustomDateInput
                                             value={filters.hadAppointmentStartDate}
                                             onChange={(value) => handleFilterChange('hadAppointmentStartDate', value)}
                                             className={styles.dateInput}
                                             placeholder="Start date"
                                         />
                                     </div>
                                     <div className={styles.dateInputContainer}>
                                         <CustomDateInput
                                             value={filters.hadAppointmentEndDate}
                                             onChange={(value) => handleFilterChange('hadAppointmentEndDate', value)}
                                             className={styles.dateInput}
                                             placeholder="End date"
                                         />
                                     </div>
                                 </div>
                                 {dateErrorHadAppointment && (
                                     <div style={{ color: '#dc2626', fontSize: 12, marginTop: 6 }}>{dateErrorHadAppointment}</div>
                                 )}
                             </>
                         )}
                    </div>
                    )}

                    {/* Had appointment with */}
                    {false && (
                    <div className={styles.filterGroup}>
                        <label className={styles.filterLabel}>Had appointment with</label>

                        <MultiSelectDropdown
                            options={Array.isArray(therapists) ? therapists.map(therapist => ({
                                value: therapist.id,
                                label: therapist.alias || therapist.name || 'Unnamed',
                                id: therapist.id,
                                gender: therapist.gender,
                                isStarred: therapist.alias && therapist.alias.includes('*')
                            })) : []}
                            selectedValues={filters.hadAppointmentWith}
                            onChange={(values) => handleFilterChange('hadAppointmentWith', values)}
                            placeholder="Select therapists"
                            allSelectedText="All therapists"
                            searchPlaceholder="Search therapists..."
                            loading={loading}
                            hasMore={false}
                            autoFocusSearch
                            onOpen={scrollToBottomSmooth}
                        />
                    </div>
                    )}

                    {/* Future appointment */}
                    {false && (
                    <div className={styles.filterGroup}>
                        <label className={styles.filterLabel}>Future appointment</label>

                        <CustomDropdown
                            options={[
                                { value: 'Has future appointment', label: 'Has future appointment' },
                                { value: 'Has no future appointment', label: 'Has no future appointment' }
                            ]}
                                 value={filters.futureAppointment}

                            onChange={(value) => handleFilterChange('futureAppointment', value)}
                            placeholder="Select option"
                            autoFocusSearch
                            onOpen={scrollToBottomSmooth}
                        />
                    </div>
                    )}

                    {/* Had service */}
                    <div className={styles.filterGroup}>
                        <label className={styles.filterLabel}>Had service</label>

                        <CustomDropdown
                            options={Array.isArray(services) ? 
                                // Create options with category headers and individual services
                                services.reduce((acc, category) => {
                                    if (category.services && Array.isArray(category.services)) {
                                        // Add category header
                                        acc.push({
                                            value: `header_${category.name}`,
                                            label: category.name,
                                            isHeader: true
                                        });
                                        
                                        // Add individual services with durations
                                        category.services.forEach(service => {
                                            const duration = service.duration || 0;
                                            const label = duration > 0 
                                                ? `${duration} Mins ${service.name}`
                                                : service.name;
                                            
                                            acc.push({
                                                value: service.id.toString(), // Use service ID instead of name_duration
                                                label: label,
                                                isHeader: false,
                                                serviceId: service.id // Store the actual service ID
                                            });
                                        });
                                    }
                                    return acc;
                                }, []) : []
                            }
                                 value={filters.hadService}

                            onChange={(value) => handleFilterChange('hadService', value)}
                            placeholder="Select service"
                            searchPlaceholder="Search services..."
                            autoFocusSearch
                            onOpen={scrollToBottomSmooth}
                        />
                    </div>

                    {/* Had service in category */}
                    {false && (
                    <div className={styles.filterGroup}>
                        <label className={styles.filterLabel}>Had service in category</label>
                        <SimpleMultiSelectDropdown
                            options={Array.isArray(serviceCategories) ? serviceCategories.map(category => ({
                                value: category.id.toString(), // Use category ID as value
                                label: category.name,
                                categoryId: category.id // Store the actual category ID
                            })) : []}
                            selectedValues={Array.isArray(filters.hadServiceInCategory) ? filters.hadServiceInCategory : []}
                            onChange={(values) => handleFilterChange('hadServiceInCategory', values)}
                            placeholder="Select categories"
                            searchPlaceholder="Search categories..."
                            autoFocusSearch
                            onOpen={scrollToBottomSmooth}
                        />
                    </div>
                    )}

                    {/* Gender */}
                    <div className={styles.filterGroup}>
                        <label className={styles.filterLabel}>Gender</label>

                        <CustomDropdown
                            options={[
                                { value: 'Any gender', label: 'Any gender' },
                                { value: 'Male', label: 'Male' },
                                { value: 'Female', label: 'Female' },
                                { value: 'Non-binary', label: 'Non-binary' },
                                { value: 'prefer-not', label: 'Prefer not to say' }
                            ]}
                                value={filters.gender}

                            onChange={(value) => handleFilterChange('gender', value)}
                            placeholder="Select gender"
                            autoFocusSearch
                        />
                    </div>

                    {/* Referred by */}
                    {/* <div className={styles.filterGroup}>
                        <label className={styles.filterLabel}>Referred by</label>
                        <input 
                            type="text"
                            placeholder="Search for referrals"
                            value={filters.referredBy}
                            onChange={(e) => handleFilterChange('referredBy', e.target.value)}
                            className={styles.textInput}
                        />
                    </div> */}

                    {/* Tag */}
                    {/* <div className={styles.filterGroup}>
                        <label className={styles.filterLabel}>Tag</label>
                        <input 
                            type="text"
                            placeholder="Filter by tag"
                            value={filters.tag}
                            onChange={(e) => handleFilterChange('tag', e.target.value)}
                            className={styles.textInput}
                        />
                    </div> */}

                    {/* Last appointment */}
                    {false && (
                    <>
                    <div className={styles.filterGroup}>
                        <div className={styles.toggleContainer}>
                            <label className={styles.filterLabel}>Last appointment</label>
                            <label className={styles.toggleSwitch}>
                                <input 
                                    type="checkbox"
                                    checked={filters.lastAppointment}
                                    onChange={(e) => handleFilterChange('lastAppointment', e.target.checked)}
                                    className={styles.toggleInput}
                                />
                                <span className={styles.toggleSlider}></span>
                            </label>
                        </div>
                    </div>

                    {filters.lastAppointment && (
                        <div className={styles.filterGroup}>
                            <label className={styles.filterLabelUnderline}>
                                More than 
                                <input 
                                    type="number"
                                    placeholder=""
                                    value={filters.daysAgo}
                                    onChange={(e) => handleFilterChange('daysAgo', e.target.value)}
                                    className={styles.inlineInput}
                                />
                                days ago
                            </label>
                        </div>
                    )}

                    {filters.lastAppointment && (
                        <div className={styles.filterGroup}>
                            <div className={styles.toggleContainer}>
                                <label className={styles.filterLabelUnderline}>Include clients with no appointments</label>
                                <label className={styles.toggleSwitch}>
                                    <input 
                                        type="checkbox"
                                        checked={filters.includeNoAppointments}
                                        onChange={(e) => handleFilterChange('includeNoAppointments', e.target.checked)}
                                        className={styles.toggleInput}
                                    />
                                    <span className={styles.toggleSlider}></span>
                                </label>
                            </div>
                        </div>
                    )}
                    </>
                    )}
                </div>

                {/* Action Buttons */}
                <div className={styles.filterActions}>
                    {/* <div className={styles.exportSection}>
                        <span className={styles.exportText}>Export</span>
                        <button className={styles.downloadButton} onClick={handleDownload} disabled={isExporting}>
                            {isExporting ? 'Downloading...' : 'Download'}
                        </button>
                    </div> */}
                    
                    <div className={styles.actionButtons}>
                        <button 
                            className={styles.clearButton} 
                            onClick={handleClearAll}
                            disabled={!hasActiveFilters()}
                            style={{ opacity: !hasActiveFilters() ? 0.5 : 1, cursor: !hasActiveFilters() ? 'not-allowed' : 'pointer' }}
                        >
                            Clear all
                        </button>
                        {hasActiveFilters() && (
                        <button className={styles.applyButton} onClick={handleApply}>
                            Apply Filter
                        </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};


export default ClientFilterSidebar; 